import React, { useState } from 'react';
import { buildMockActionHistorySample } from './buildMockActionHistorySample';

/** Default ladder mock when enabling (indices 0–9; index 9 = General / honor #) */
const DEV_RANK_MOCK_DEFAULT = {
  player: { honorRank: 1, pvp_ladder_rank_index: 0, pvp_in_league: false },
  opponent: { honorRank: 2, pvp_ladder_rank_index: 3, pvp_in_league: false },
};

const LADDER_INDEX_OPTIONS = [
  [0, 'Second Lieutenant'],
  [1, 'First Lieutenant'],
  [2, 'Captain'],
  [3, 'Major'],
  [4, 'Lieutenant Colonel'],
  [5, 'Colonel'],
  [6, 'Brigadier General'],
  [7, 'Major General'],
  [8, 'Lieutenant General'],
  [9, 'General (honor #)'],
];

const RANK_PRESET_OPTIONS = [
  { value: 'none', label: 'Hide badge (no rank)' },
  { value: 'insignia_0', label: 'Insignia: Second Lieutenant' },
  { value: 'insignia_1', label: 'Insignia: First Lieutenant' },
  { value: 'insignia_2', label: 'Insignia: Captain' },
  { value: 'insignia_3', label: 'Insignia: Major' },
  { value: 'insignia_4', label: 'Insignia: Lieutenant Colonel' },
  { value: 'insignia_5', label: 'Insignia: Colonel' },
  { value: 'insignia_6', label: 'Insignia: Brigadier General' },
  { value: 'insignia_7', label: 'Insignia: Major General' },
  { value: 'insignia_8', label: 'Insignia: Lieutenant General' },
  { value: 'number_1', label: 'Number: #1 (Top 10 style)' },
  { value: 'number_25', label: 'Number: #25 (Top 50 style)' },
  { value: 'number_75', label: 'Number: #75 (Top 100 style)' },
  { value: 'number_250', label: 'Number: #250 (Top 500 style)' },
  { value: 'number_750', label: 'Number: #750 (Default style)' },
];

function resolveRankPresetValue(row) {
  if (row.honorRank === '' || row.honorRank === undefined || row.honorRank === null) {
    return 'none';
  }
  if (row.pvp_in_league !== true && Number.isFinite(Number(row.pvp_ladder_rank_index)) && Number(row.pvp_ladder_rank_index) >= 0 && Number(row.pvp_ladder_rank_index) <= 8) {
    return `insignia_${Number(row.pvp_ladder_rank_index)}`;
  }
  const honorNum = Number(row.honorRank);
  if (honorNum === 1) return 'number_1';
  if (honorNum === 25) return 'number_25';
  if (honorNum === 75) return 'number_75';
  if (honorNum === 250) return 'number_250';
  if (honorNum === 750) return 'number_750';
  return '';
}

function getRankPresetPatch(presetValue) {
  if (presetValue === 'none') {
    return {
      honorRank: '',
      pvp_ladder_rank_index: 9,
      pvp_in_league: true,
    };
  }

  if (presetValue.startsWith('insignia_')) {
    const idx = parseInt(presetValue.replace('insignia_', ''), 10);
    if (!Number.isFinite(idx) || idx < 0 || idx > 8) {
      throw new Error(`DeveloperConsole: invalid insignia preset: ${presetValue}`);
    }
    return {
      honorRank: 1,
      pvp_ladder_rank_index: idx,
      pvp_in_league: false,
    };
  }

  if (presetValue.startsWith('number_')) {
    const honor = parseInt(presetValue.replace('number_', ''), 10);
    if (!Number.isFinite(honor) || honor < 1) {
      throw new Error(`DeveloperConsole: invalid number preset: ${presetValue}`);
    }
    return {
      honorRank: honor,
      pvp_ladder_rank_index: 9,
      pvp_in_league: true,
    };
  }

  throw new Error(`DeveloperConsole: unknown rank preset: ${presetValue}`);
}

const DeveloperConsole = ({ 
  ws, 
  gameId, 
  devUnitSearch, 
  setDevUnitSearch,
  showGridOverlay,
  setShowGridOverlay,
  showDebugOverlay,
  setShowDebugOverlay,
  showTargetedDebug,
  setShowTargetedDebug,
  showElementSelector,
  setShowElementSelector,
  showEditableOverlay,
  setShowEditableOverlay,
  gridSize,
  setGridSize,
  targetElements,
  setTargetElements,
  handleElementSelected,
  handleElementMoved,
  handleElementResized,
  handleSaveDesign,
  savedDesign,
  setSavedDesign,
  devRankMock,
  setDevRankMock,
  onHideDevElementsForScreenshot,
  hideDevElementsButtonLabel,
  onOpenSetup,
  setActionLog,
  userId,
  opponentId,
  /** { registerAnimation, gameState, userId } — preview Betrayal (341) drone flash on your row as if opponent cast */
  devBetrayalPlayerDronePreview,
}) => {
  const [isConsoleMinimized, setIsConsoleMinimized] = useState(false);
  const [forceHighlightEnemy, setForceHighlightEnemy] = useState(Boolean(typeof window !== 'undefined' && window.FORCE_HIGHLIGHT_ENEMY));

  return (
    <div 
      id="developer-console"
      style={{
        position: 'fixed',
        bottom: '20px',
        left: '20px',
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        color: 'white',
        padding: isConsoleMinimized ? '5px' : '10px',
        borderRadius: '8px',
        border: '2px solid #ff6b35',
        /* Above battle dock / O–S–C strip (BattleUI player-dock-row z-index 1200+); below deploy-drag overlay (~100002). */
        zIndex: 20000,
        minWidth: isConsoleMinimized ? '80px' : '300px',
        maxHeight: isConsoleMinimized ? '40px' : '400px',
        overflow: 'auto',
        cursor: 'move',
        userSelect: 'none',
        transition: 'all 0.3s ease'
      }}
      onMouseDown={(e) => {
        if (e.target.id === 'developer-console' || e.target.closest('#developer-console')) {
          const console = document.getElementById('developer-console');
          let isDragging = false;
          let startX = e.clientX - console.offsetLeft;
          let startY = e.clientY - console.offsetTop;

          const handleMouseMove = (e) => {
            if (isDragging) {
              console.style.left = (e.clientX - startX) + 'px';
              console.style.top = (e.clientY - startY) + 'px';
              console.style.bottom = 'auto';
            }
          };

          const handleMouseUp = () => {
            isDragging = false;
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
          };

          isDragging = true;
          document.addEventListener('mousemove', handleMouseMove);
          document.addEventListener('mouseup', handleMouseUp);
        }
      }}
    >
      {/* Minimize / hide-for-screenshots */}
      <div style={{
        position: 'absolute',
        top: '5px',
        right: '5px',
        zIndex: 1001,
        display: 'flex',
        gap: '6px',
        alignItems: 'center',
      }}>
        {typeof onHideDevElementsForScreenshot === 'function' ? (
          <button
            type="button"
            onClick={() => onHideDevElementsForScreenshot()}
            title={hideDevElementsButtonLabel}
            style={{
              backgroundColor: '#2e7d32',
              color: 'white',
              border: 'none',
              padding: '4px 8px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '10px',
              fontWeight: 'bold',
              maxWidth: '140px',
              lineHeight: 1.2,
            }}
          >
            {hideDevElementsButtonLabel ?? 'Hide dev UI until refresh'}
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => setIsConsoleMinimized(!isConsoleMinimized)}
          style={{
            backgroundColor: '#ff6b35',
            color: 'white',
            border: 'none',
            padding: '4px 8px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '10px',
            fontWeight: 'bold'
          }}
        >
          {isConsoleMinimized ? 'Maximize' : 'Minimize'}
        </button>
      </div>

      {/* Console Content - Only show when not minimized */}
      {!isConsoleMinimized && (
        <>
          <div style={{ color: '#ff6b35', fontWeight: 'bold', marginBottom: '10px' }}>
            🛠️ Developer Console
          </div>

          {typeof onOpenSetup === 'function' ? (
            <div style={{ marginBottom: '10px' }}>
              <button
                type="button"
                onClick={() => onOpenSetup()}
                style={{
                  margin: '2px',
                  padding: '6px 12px',
                  fontSize: '12px',
                  color: '#111',
                  backgroundColor: '#ffb74d',
                  border: '1px solid #ff9800',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  width: '100%',
                }}
              >
                Setup
              </button>
            </div>
          ) : null}
      
          {/* Enemy Zone Controls */}
          <div style={{ marginBottom: '10px' }}>
            <div style={{ color: '#ffaa00', marginBottom: '5px' }}>Enemy Zones:</div>
            <button onClick={() => {
              const orderId = prompt('Enter order ID (default: 99):', '99');
              if (orderId && ws && ws.readyState === 1) {
                console.log(`🛠️ DEV: Filling enemy flank with order ${orderId}`);
                ws.send(JSON.stringify({
                  type: "play-turn",
                  gameId,
                  action: { 
                    type: "devFillZone", 
                    zone: "flank",
                    orderId: parseInt(orderId),
                    targetPlayer: "opponent"
                  },
                }));
              }
            }} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              Fill Enemy Flank
            </button>
            <button onClick={() => {
              const orderId = prompt('Enter order ID (default: 99):', '99');
              if (orderId && ws && ws.readyState === 1) {
                console.log(`🛠️ DEV: Filling enemy front with order ${orderId}`);
                ws.send(JSON.stringify({
                  type: "play-turn",
                  gameId,
                  action: { 
                    type: "devFillZone", 
                    zone: "front",
                    orderId: parseInt(orderId),
                    targetPlayer: "opponent"
                  },
                }));
              }
            }} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              Fill Enemy Front
            </button>
            <button onClick={() => {
              if (ws && ws.readyState === 1) {
                console.log('🛠️ DEV: Clear enemy flank');
                ws.send(JSON.stringify({
                  type: "play-turn",
                  gameId,
                  action: { 
                    type: "devClearZone", 
                    zone: "flank",
                    targetPlayer: "opponent"
                  },
                }));
              }
            }} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              Clear Enemy Flank
            </button>
            <button onClick={() => {
              if (ws && ws.readyState === 1) {
                console.log('🛠️ DEV: Clear enemy front');
                ws.send(JSON.stringify({
                  type: "play-turn",
                  gameId,
                  action: { 
                    type: "devClearZone", 
                    zone: "front",
                    targetPlayer: "opponent"
                  },
                }));
              }
            }} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              Clear Enemy Front
            </button>
          </div>

          {/* Player Controls */}
          <div style={{ marginBottom: '10px' }}>
            <div style={{ color: '#ffaa00', marginBottom: '5px' }}>Player:</div>
            <button onClick={() => {
              if (ws && ws.readyState === 1) {
                console.log('🛠️ DEV: Clear player hand');
                ws.send(JSON.stringify({
                  type: "play-turn",
                  gameId,
                  action: { 
                    type: "devClearHand"
                  },
                }));
              }
            }} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              Clear Hand
            </button>
            <button onClick={() => {
              if (ws && ws.readyState === 1) {
                console.log('🛠️ DEV: Clear player front');
                ws.send(JSON.stringify({
                  type: "play-turn",
                  gameId,
                  action: { 
                    type: "devClearZone", 
                    zone: "front",
                    targetPlayer: "player"
                  },
                }));
              }
            }} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              Clear Front
            </button>
            <button onClick={() => {
              if (ws && ws.readyState === 1) {
                console.log('🛠️ DEV: Clear player flank');
                ws.send(JSON.stringify({
                  type: "play-turn",
                  gameId,
                  action: { 
                    type: "devClearZone", 
                    zone: "flank",
                    targetPlayer: "player"
                  },
                }));
              }
            }} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              Clear Flank
            </button>
          </div>

          {/* Drone Controls */}
          <div style={{ marginBottom: '10px' }}>
            <div style={{ color: '#ffaa00', marginBottom: '5px' }}>Drone Deployment:</div>
            <button onClick={() => {
              const orderId = prompt('Enter drone ID (default: 18):', '18');
              if (orderId && ws && ws.readyState === 1) {
                console.log(`🛠️ DEV: Deploy drone ${orderId} for opponent`);
                ws.send(JSON.stringify({
                  type: "play-turn",
                  gameId,
                  action: { 
                    type: "devDeployDrone", 
                    orderId: parseInt(orderId),
                    targetPlayer: "opponent"
                  },
                }));
              }
            }} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              Deploy Opponent Drone
            </button>
            <button onClick={() => {
              const orderId = prompt('Enter drone ID (default: 18):', '18');
              if (orderId && ws && ws.readyState === 1) {
                console.log(`🛠️ DEV: Deploy drone ${orderId} for self`);
                ws.send(JSON.stringify({
                  type: "play-turn",
                  gameId,
                  action: { 
                    type: "devDeployDrone", 
                    orderId: parseInt(orderId),
                    targetPlayer: "self"
                  },
                }));
              }
            }} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              Deploy My Drone
            </button>
            <button
              type="button"
              onClick={() => {
                if (ws && ws.readyState === 1) {
                  console.log('🛠️ DEV: Bot equip weapon 63 on random deployed unit');
                  ws.send(JSON.stringify({
                    type: 'play-turn',
                    gameId,
                    action: {
                      type: 'devBotEquipWeapon',
                      weaponId: 63,
                      targetPlayer: 'opponent',
                    },
                  }));
                }
              }}
              style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black', display: 'block', marginTop: '6px' }}
            >
              Bot: equip weapon 63 (random unit)
            </button>
            <button
              type="button"
              onClick={() => {
                if (ws && ws.readyState === 1) {
                  console.log('🛠️ DEV: Bot executes Venom Cloud (318)');
                  ws.send(JSON.stringify({
                    type: 'play-turn',
                    gameId,
                    action: {
                      type: 'devBotExecuteVenomCloud',
                      strategemId: 318,
                    },
                  }));
                }
              }}
              style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black', display: 'block' }}
            >
              Bot: execute Venom Cloud
            </button>
            <button
              type="button"
              onClick={() => {
                if (ws && ws.readyState === 1) {
                  console.log('🛠️ DEV: Bot Precision Strike (321) → random target');
                  ws.send(JSON.stringify({
                    type: 'play-turn',
                    gameId,
                    action: {
                      type: 'devBotExecuteStrategemRandomTarget',
                      strategemId: 321,
                    },
                  }));
                }
              }}
              style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black', display: 'block' }}
            >
              Bot: Precision Strike (random target)
            </button>
            <button
              type="button"
              onClick={() => {
                if (ws && ws.readyState === 1) {
                  console.log('🛠️ DEV: Bot Guided Missile (317) → random target');
                  ws.send(JSON.stringify({
                    type: 'play-turn',
                    gameId,
                    action: {
                      type: 'devBotExecuteStrategemRandomTarget',
                      strategemId: 317,
                    },
                  }));
                }
              }}
              style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black', display: 'block' }}
            >
              Bot: Guided Missile (random target)
            </button>
            {devBetrayalPlayerDronePreview?.registerAnimation && devBetrayalPlayerDronePreview?.gameState && devBetrayalPlayerDronePreview?.userId != null && (
              <button
                type="button"
                onClick={() => {
                  const { registerAnimation, gameState: gs, userId: uid } = devBetrayalPlayerDronePreview;
                  const myState = gs?.state?.[uid];
                  const rows = myState?.drones;
                  if (!Array.isArray(rows) || rows.length === 0) {
                    console.error('DeveloperConsole: no drones in state for user — deploy a drone first.');
                    return;
                  }
                  const visible = rows.filter((d) => d && !d.markedForRemoval);
                  if (visible.length === 0) {
                    console.error('DeveloperConsole: no visible (non-removed) drones to target.');
                    return;
                  }
                  const drone = visible[0];
                  if (!drone.instanceId) {
                    console.error('DeveloperConsole: drone missing instanceId.');
                    return;
                  }
                  // Player UI uses `data-drone-index` = index in the filtered (visible) list, not raw array index.
                  const domIndex = 0;
                  const generalElement = document.querySelector('[data-zone="opponent_general"]');
                  let fromPosition = { x: window.innerWidth - 200, y: 100 };
                  if (generalElement) {
                    const rect = generalElement.getBoundingClientRect();
                    fromPosition = {
                      x: rect.left + rect.width / 2,
                      y: rect.top + rect.height / 2,
                    };
                  }
                  const sid = `dev_betrayal_your_drone_${Date.now()}`;
                  registerAnimation({
                    fromHand: { instanceId: sid, position: fromPosition },
                    toSlot: { zone: 'your_drone', index: domIndex, playerId: 'your' },
                    targetType: 'drone',
                    strategemId: 341,
                    strategemInstanceId: sid,
                    targetInstanceId: drone.instanceId,
                    animationKey: `${sid}-anim`,
                    isOpponentStrategem: true,
                    isDevBetrayalDronePreview: true,
                    devBetrayalRemoveUserId: uid,
                  });
                  console.log('DeveloperConsole: queued local Betrayal (341) preview on your drone slot', {
                    domIndex,
                    instanceId: drone.instanceId,
                  });
                }}
                title="Local-only: plays VFX then removes that drone from client state (no server). Use for layout testing."
                style={{ margin: '2px', padding: '4px 8px', fontSize: '11px', color: 'black', display: 'block', marginTop: '6px' }}
              >
                Preview Betrayal on my drone (341)
              </button>
            )}
          </div>

        {/* Targeting Debug Controls */}
        <div style={{ marginBottom: '10px' }}>
          <div style={{ color: '#ffaa00', marginBottom: '5px' }}>Targeting Debug:</div>
          <button
            onClick={() => {
              const next = !forceHighlightEnemy;
              setForceHighlightEnemy(next);
              try {
                if (typeof window !== 'undefined') {
                  window.FORCE_HIGHLIGHT_ENEMY = next;
                  // Notify app to re-render targeting
                  window.dispatchEvent(new Event('force-highlight-change'));
                }
              } catch (e) {}
              console.log(`🛠️ DEV: FORCE_HIGHLIGHT_ENEMY set to`, next);
            }}
            style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black', backgroundColor: forceHighlightEnemy ? '#ff0' : '#333' }}
          >
            {forceHighlightEnemy ? 'Disable Target Enemy Units' : 'Target Enemy Units'}
          </button>
        </div>

          {/* Player Stats Controls */}
          <div style={{ marginBottom: '10px' }}>
            <div style={{ color: '#ffaa00', marginBottom: '5px' }}>Player Stats:</div>
            <button onClick={() => {
              const intelligence = prompt('Set current intelligence (default: 50):', '50');
              if (intelligence && ws && ws.readyState === 1) {
                console.log(`🛠️ DEV: Set intelligence to ${intelligence}`);
                ws.send(JSON.stringify({
                  type: "play-turn",
                  gameId,
                  action: { 
                    type: "devSetIntelligence", 
                    intelligence: parseInt(intelligence)
                  },
                }));
              }
            }} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              Set Intelligence
            </button>
            <button onClick={() => {
              const maxIntelligence = prompt('Set max intelligence (default: 5):', '5');
              if (maxIntelligence && ws && ws.readyState === 1) {
                console.log(`🛠️ DEV: Set max intelligence to ${maxIntelligence}`);
                ws.send(JSON.stringify({
                  type: "play-turn",
                  gameId,
                  action: { 
                    type: "devSetMaxIntelligence", 
                    maxIntelligence: parseInt(maxIntelligence)
                  },
                }));
              }
            }} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              Set Max Intelligence
            </button>
            <button onClick={() => {
              const health = prompt('Set player health (default: 30):', '30');
              if (health && ws && ws.readyState === 1) {
                console.log(`🛠️ DEV: Set health to ${health}`);
                ws.send(JSON.stringify({
                  type: "play-turn",
                  gameId,
                  action: { 
                    type: "devSetHealth", 
                    health: parseInt(health)
                  },
                }));
              }
            }} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              Set Health
            </button>
            <button onClick={() => {
              if (ws && ws.readyState === 1) {
                console.log('🛠️ DEV: Spawn unit 99 in enemy front and attack');
                ws.send(JSON.stringify({
                  type: "play-turn",
                  gameId,
                  action: { 
                    type: "devSpawnAndAttack", 
                    unitId: 99,
                    zone: "opponent_front"
                  },
                }));
              }
            }} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              Spawn Unit 99 & Attack
            </button>
          </div>

          {/* Unit Addition */}
          <div style={{ marginBottom: '10px' }}>
            <div style={{ color: '#ffaa00', marginBottom: '5px' }}>Add Unit to Hand:</div>
            <input 
              type="text" 
              placeholder="Search unit name or ID..."
              value={devUnitSearch}
              onChange={(e) => setDevUnitSearch(e.target.value)}
              style={{ 
                width: '100%', 
                padding: '4px', 
                marginBottom: '5px',
                backgroundColor: '#333',
                color: 'white',
                border: '1px solid #555'
              }}
            />
            <button onClick={() => {
              if (devUnitSearch && ws && ws.readyState === 1) {
                console.log(`🛠️ DEV: Add unit ${devUnitSearch} to hand`);
                ws.send(JSON.stringify({
                  type: "play-turn",
                  gameId,
                  action: { 
                    type: "devDeployUnit", 
                    unitId: parseInt(devUnitSearch) || devUnitSearch
                  },
                }));
              }
            }} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              Add Selected Unit to Hand
            </button>
          </div>

          {/* Grid Overlay Controls */}
          <div style={{ marginBottom: '10px' }}>
            <div style={{ color: '#ffaa00', marginBottom: '5px' }}>Layout Debugging:</div>
            <button onClick={() => setShowGridOverlay(!showGridOverlay)} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              {showGridOverlay ? 'Hide Grid' : 'Show Grid'}
            </button>
            <button onClick={() => setShowDebugOverlay(!showDebugOverlay)} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              {showDebugOverlay ? 'Hide All Boundaries' : 'Show All Boundaries'}
            </button>
            <button onClick={() => setShowTargetedDebug(!showTargetedDebug)} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              {showTargetedDebug ? 'Hide Targeted Boundaries' : 'Show Targeted Boundaries'}
            </button>
            <div style={{ marginTop: '5px' }}>
              <label style={{ color: '#ccc', fontSize: '10px' }}>Grid Size: </label>
              <input 
                type="number" 
                value={gridSize} 
                onChange={(e) => setGridSize(parseInt(e.target.value) || 20)}
                style={{ width: '50px', padding: '2px', backgroundColor: '#333', color: 'white', border: '1px solid #555' }}
                min="10"
                max="100"
              />
            </div>
          </div>

          {/* State Sync Monitor Controls */}
          <div style={{ marginBottom: '10px' }}>
            <div style={{ color: '#ffaa00', marginBottom: '5px' }}>🚦 State Sync Monitor:</div>
            <button 
              onClick={() => {
                const isEnabled = localStorage.getItem('enableStateSyncMonitor') === 'true';
                if (isEnabled) {
                  localStorage.removeItem('enableStateSyncMonitor');
                  console.log('🛠️ DEV: State Sync Monitor disabled');
                } else {
                  localStorage.setItem('enableStateSyncMonitor', 'true');
                  console.log('🛠️ DEV: State Sync Monitor enabled');
                }
                // Force page reload to apply changes
                window.location.reload();
              }}
              style={{ 
                margin: '2px', 
                padding: '4px 8px', 
                fontSize: '12px', 
                backgroundColor: localStorage.getItem('enableStateSyncMonitor') === 'true' ? '#4CAF50' : '#F44336',
                color: 'white',
                fontWeight: 'bold',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {localStorage.getItem('enableStateSyncMonitor') === 'true' ? '🟢 Monitor ON' : '🔴 Monitor OFF'}
            </button>
            <div style={{ fontSize: '10px', color: '#ccc', marginTop: '5px' }}>
              Monitors frontend/backend state synchronization
            </div>
          </div>

          {/* Targeted Element Controls */}
          <div style={{ marginBottom: '10px' }}>
            <div style={{ color: '#ffaa00', marginBottom: '5px' }}>Target Specific Elements:</div>
            <button onClick={() => setShowElementSelector(!showElementSelector)} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              {showElementSelector ? 'Hide Element Selector' : 'Show Element Selector'}
            </button>
            <button onClick={() => setTargetElements([])} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black' }}>
              Clear All
            </button>
          </div>

          {/* Editable Design Controls */}
          <div style={{ marginBottom: '10px' }}>
            <div style={{ color: '#ffaa00', marginBottom: '5px' }}>🎨 Design Mode:</div>
            <button onClick={() => setShowEditableOverlay(!showEditableOverlay)} style={{ margin: '2px', padding: '4px 8px', fontSize: '12px', color: 'black', backgroundColor: showEditableOverlay ? '#ff00ff' : '#333' }}>
              {showEditableOverlay ? 'Exit Design Mode' : 'Enter Design Mode'}
            </button>
            <div style={{ fontSize: '10px', color: '#ccc', marginTop: '5px' }}>
              {showEditableOverlay ? 'Drag elements to move, resize from corner' : 'Enable to drag & resize UI elements'}
            </div>
            <div style={{ color: '#ccc', fontSize: '10px', marginTop: '5px' }}>
              Targeted: {targetElements.length} elements
            </div>
            <button
              onClick={() => {
                localStorage.removeItem('battleUI_design');
                setSavedDesign({});
                alert('Design changes cleared! Page will reload to reset elements.');
                window.location.reload();
              }}
              style={{
                backgroundColor: '#ff4444',
                color: 'white',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '10px',
                fontWeight: 'bold',
                marginTop: '5px'
              }}
            >
              Clear Saved Design
            </button>
          </div>

          {typeof setDevRankMock === 'function' && (
            <div style={{ marginBottom: '10px' }}>
              <div style={{ color: '#ffaa00', marginBottom: '5px' }}>PvP rank badges (local UI):</div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', color: '#ccc', marginBottom: '8px' }}>
                <input
                  type="checkbox"
                  checked={devRankMock != null}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setDevRankMock(DEV_RANK_MOCK_DEFAULT);
                    } else {
                      setDevRankMock(null);
                    }
                  }}
                />
                Mock rank badges (player + opponent)
              </label>
              {devRankMock != null && (
                <div style={{ fontSize: '11px', color: '#ddd' }}>
                  {(['player', 'opponent']).map((side) => {
                    const label = side === 'player' ? 'Player' : 'Opponent';
                    const row = devRankMock[side];
                    return (
                      <div key={side} style={{ marginBottom: '8px', paddingLeft: '4px', borderLeft: '2px solid #555' }}>
                        <div style={{ color: '#ffaa00', marginBottom: '4px' }}>{label}</div>
                        <div style={{ marginBottom: '4px' }}>
                          <label style={{ color: '#aaa', fontSize: '10px' }}>Quick preset</label>
                          <select
                            value={resolveRankPresetValue(row)}
                            onChange={(e) => {
                              const patch = getRankPresetPatch(e.target.value);
                              setDevRankMock((prev) => ({
                                ...prev,
                                [side]: {
                                  ...prev[side],
                                  ...patch,
                                },
                              }));
                            }}
                            style={{
                              marginLeft: '6px',
                              maxWidth: '220px',
                              padding: '2px',
                              backgroundColor: '#333',
                              color: 'white',
                              border: '1px solid #555',
                              fontSize: '11px',
                            }}
                          >
                            <option value="">Custom (manual values)</option>
                            {RANK_PRESET_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div style={{ marginBottom: '4px' }}>
                          <label style={{ color: '#aaa', fontSize: '10px' }}>Ladder rank</label>
                          <select
                            value={row.pvp_ladder_rank_index}
                            onChange={(e) => {
                              const idx = Math.max(0, Math.min(9, parseInt(e.target.value, 10) || 0));
                              setDevRankMock((prev) => ({
                                ...prev,
                                [side]: {
                                  ...prev[side],
                                  pvp_ladder_rank_index: idx,
                                  pvp_in_league: idx === 9,
                                },
                              }));
                            }}
                            style={{
                              marginLeft: '6px',
                              maxWidth: '220px',
                              padding: '2px',
                              backgroundColor: '#333',
                              color: 'white',
                              border: '1px solid #555',
                              fontSize: '11px',
                            }}
                          >
                            {LADDER_INDEX_OPTIONS.map(([idx, name]) => (
                              <option key={idx} value={idx}>
                                {idx} — {name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label style={{ color: '#aaa', fontSize: '10px' }}>Honor # (MMR position)</label>
                          <input
                            type="number"
                            min={1}
                            value={row.honorRank === '' || row.honorRank === undefined ? '' : row.honorRank}
                            onChange={(e) => {
                              const raw = e.target.value;
                              if (raw === '') {
                                setDevRankMock((prev) => ({
                                  ...prev,
                                  [side]: { ...prev[side], honorRank: '' },
                                }));
                                return;
                              }
                              const v = parseInt(raw, 10);
                              if (!Number.isFinite(v)) {
                                throw new Error(`DeveloperConsole: invalid honor rank: ${raw}`);
                              }
                              setDevRankMock((prev) => ({
                                ...prev,
                                [side]: { ...prev[side], honorRank: v },
                              }));
                            }}
                            style={{
                              marginLeft: '6px',
                              width: '72px',
                              padding: '2px',
                              backgroundColor: '#333',
                              color: 'white',
                              border: '1px solid #555',
                              fontSize: '11px',
                            }}
                          />
                        </div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px', fontSize: '10px', color: '#999' }}>
                          <input
                            type="checkbox"
                            checked={row.pvp_in_league === true}
                            onChange={(e) => {
                              setDevRankMock((prev) => ({
                                ...prev,
                                [side]: {
                                  ...prev[side],
                                  pvp_in_league: e.target.checked,
                                },
                              }));
                            }}
                          />
                          In league (affects # vs insignia with ladder data)
                        </label>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div style={{ marginBottom: '10px', marginTop: '8px' }}>
            <div style={{ color: '#ffaa00', marginBottom: '5px' }}>Battle log:</div>
            <button
              type="button"
              onClick={() => {
                if (typeof setActionLog !== 'function') return;
                setActionLog(buildMockActionHistorySample({ userId, opponentId }));
              }}
              title="Client-only: replaces the drawer log until the next server game-update. One row per history event type for copy review."
              style={{
                margin: '2px',
                padding: '6px 10px',
                fontSize: '12px',
                color: '#111',
                backgroundColor: '#ce93d8',
                border: '1px solid #ab47bc',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Mock full history (all types)
            </button>
          </div>

          <div style={{ fontSize: '10px', color: '#888', marginTop: '10px' }}>
            Developer Mode Active
          </div>
        </>
      )}
    </div>
  );
};

export default DeveloperConsole;
